import React, { useState, useRef, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { FormControl, InputLabel, Select as MuiSelect, MenuItem, TextField } from "@mui/material";

const UserConsultantOnlineGapChart = ({ data, filter, onFilterChange, onDateChange, onStartDateChange, onEndDateChange, selectedDate, startDate, endDate, intervalGap, onIntervalChange }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const setHoverRef = useRef(setHoverIndex);
  useEffect(() => { setHoverRef.current = setHoverIndex; }, []);

  // Generate 2-hour interval labels (00:00-02:00, 02:00-04:00, etc.)
  const generateTimeIntervals = () => {
    const intervals = [];
    for (let i = 0; i < 24; i += 2) {
      const startHour = i.toString().padStart(2, '0');
      const endHour = (i + 2).toString().padStart(2, '0');
      intervals.push(`${startHour}:00-${endHour}:00`);
    }
    return intervals;
  };

  const timeIntervals = generateTimeIntervals();
  const categories = data?.timeIntervals || timeIntervals;

  // Get interval label for chart titles
  const getIntervalLabel = () => {
    switch(intervalGap) {
      case '15min':
        return '15-Minute';
      case '30min':
        return '30-Minute';
      case '1hour':
        return '1-Hour';
      case '2hour':
        return '2-Hour';
      case '6hour':
        return '6-Hour';
      case '12hour':
        return '12-Hour';
      default:
        return '2-Hour';
    }
  };

  // Create label formatter functions with access to categories
  const getLabelFormatter = () => {
    if (intervalGap === '15min') {
      return function(value) {
        try {
          const index = categories.indexOf(value);
          return index !== -1 && index % 4 === 0 ? value : '';
        } catch (e) {
          return value;
        }
      };
    } else if (intervalGap === '30min') {
      return function(value) {
        try {
          const index = categories.indexOf(value);
          return index !== -1 && index % 2 === 0 ? value : '';
        } catch (e) {
          return value;
        }
      };
    }
    return undefined;
  };

  const options = {
    chart: {
      type: 'line',
      height: 400,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      events: (() => {
        const ev = {
          dataPointMouseEnter: function(event, chartContext, config) {
            const idx = config && config.dataPointIndex;
            if (typeof idx === 'number' && idx >= 0) setTimeout(() => setHoverRef.current(idx), 0);
          },
          dataPointMouseLeave: function() { setTimeout(() => setHoverRef.current(null), 0); },
          dataPointSelection: function(event, chartContext, config) {
            const idx = config && config.dataPointIndex;
            if (typeof idx === 'number' && idx >= 0) setTimeout(() => setHoverRef.current(idx), 0);
          },
          mouseLeave: function() { setTimeout(() => setHoverRef.current(null), 0); }
        };
        return ev;
      })()
    },
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
    stroke: {
      curve: 'smooth',
      width: 3
    },
    markers: {
      size: 8,
      hover: { size: 10 }
    },
    xaxis: {
      categories: categories,
      title: {
        text: `Time Intervals (${getIntervalLabel()} gaps)`,
        style: {
          fontSize: '14px',
          fontWeight: '600',
          color: '#666'
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: intervalGap === '15min' ? '9px' : '11px'
        },
        rotate: intervalGap === '15min' || intervalGap === '30min' ? -90 : -45,
        rotateAlways: intervalGap === '15min' || intervalGap === '30min',
        showDuplicates: false,
        hideOverlappingLabels: true,
        // For smaller intervals, show fewer labels to avoid crowding
        ...(intervalGap === '15min' && {
          maxHeight: 100,
          formatter: getLabelFormatter()
        }),
        ...(intervalGap === '30min' && {
          maxHeight: 100,
          formatter: getLabelFormatter()
        })
      }
    },
    yaxis: {
      title: {
        text: 'Number of Users/Consultants',
        style: {
          fontSize: '14px',
          fontWeight: '600',
          color: '#666'
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '12px'
        },
        formatter: function (val) {
          return Math.round(val);
        }
      }
    },
    title: {
      text: `User/Consultant Online Gap & Consultation Status (${getIntervalLabel()} Intervals)`,
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#242424'
      }
    },
    subtitle: {
      text: data?.dateLabel || '',
      align: 'left',
      style: {
        fontSize: '12px',
        color: '#666'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '14px',
      fontWeight: 600,
      markers: {
        width: 12,
        height: 12,
        radius: 6
      }
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      custom: function({ dataPointIndex, w }) {
        if (dataPointIndex != null && w?.globals?.labels) {
          setTimeout(() => setHoverRef.current(dataPointIndex), 0);
        }
        return '';
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 300
          },
          xaxis: {
            labels: {
              rotate: -90,
              rotateAlways: true
            }
          }
        }
      }
    ]
  };

  const series = [
    {
      name: 'Users Online',
      data: data?.usersOnline || []
    },
    {
      name: 'Consultants Online',
      data: data?.consultantsOnline || []
    },
    {
      name: 'Missed Consultations',
      data: data?.missedConsultations || []
    },
    {
      name: 'Cancelled Consultations',
      data: data?.cancelledConsultations || []
    }
  ];

  const filterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_week', label: 'Last Week (Average)' },
    { value: 'last_month', label: 'Last Month (Average)' },
    { value: 'current_year', label: 'Current Year (Average)' },
    { value: 'last_year', label: 'Last Year (Average)' },
    { value: 'custom', label: 'Custom Range (Average)' }
  ];

  return (
    <div className="chart-container" style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      {/* Filter Controls */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '180px' }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Filter</InputLabel>
            <MuiSelect
              value={filter || 'today'}
              onChange={(e) => onFilterChange(e.target.value)}
              label="Filter"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3b82f6',
                },
              }}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </div>
        
        <div style={{ minWidth: '180px' }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Time Gap</InputLabel>
            <MuiSelect
              value={intervalGap || '2hour'}
              onChange={(e) => onIntervalChange(e.target.value)}
              label="Time Gap"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3b82f6',
                },
              }}
            >
              <MenuItem value="15min">15 Minutes</MenuItem>
              <MenuItem value="30min">30 Minutes</MenuItem>
              <MenuItem value="1hour">1 Hour</MenuItem>
              <MenuItem value="2hour">2 Hours</MenuItem>
              <MenuItem value="6hour">6 Hours</MenuItem>
              <MenuItem value="12hour">12 Hours</MenuItem>
            </MuiSelect>
          </FormControl>
        </div>
        
        {filter === 'custom' && (
          <>
            <div style={{ minWidth: '180px' }}>
              <TextField
                size="small"
                type="date"
                variant="outlined"
                label="Start Date"
                value={startDate || ''}
                onChange={(e) => onStartDateChange && onStartDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#3b82f6',
                  },
                }}
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <TextField
                size="small"
                type="date"
                variant="outlined"
                label="End Date"
                value={endDate || ''}
                onChange={(e) => onEndDateChange && onEndDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                inputProps={{
                  min: startDate || undefined
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#3b82f6',
                  },
                }}
              />
            </div>
            {/* Backward compatibility: single date picker */}
            {!startDate && !endDate && (
              <div style={{ minWidth: '180px' }}>
                <TextField
                  size="small"
                  type="date"
                  variant="outlined"
                  label="Select Date"
                  value={selectedDate || ''}
                  onChange={(e) => onDateChange && onDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#3b82f6',
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Chart - card stays until cursor leaves this block */}
      <div style={{ position: 'relative' }} onMouseLeave={() => setHoverIndex(null)}>
        {hoverIndex != null && (() => {
          const cats = data?.timeIntervals || timeIntervals;
          const usersArr = data?.usersOnline || [];
          const consultantsArr = data?.consultantsOnline || [];
          const missedArr = data?.missedConsultations || [];
          const cancelledArr = data?.cancelledConsultations || [];
          const category = cats[hoverIndex] ?? 'N/A';
          const userVal = usersArr[hoverIndex] ?? 0;
          const consultantVal = consultantsArr[hoverIndex] ?? 0;
          const missedVal = missedArr[hoverIndex] ?? 0;
          const cancelledVal = cancelledArr[hoverIndex] ?? 0;
          let totalU = 0, totalC = 0;
          for (let j = 0; j < usersArr.length; j++) {
            totalU += Number(usersArr[j]) || 0;
            totalC += Number(consultantsArr[j]) || 0;
          }
          const demandSupplyRatio = consultantVal > 0 ? userVal / consultantVal : 0;
          const overallRatio = totalC > 0 ? totalU / totalC : 0;
          const cardStyle = {
            position: 'absolute', top: 16, right: 16, zIndex: 10,
            padding: 16, background: '#fff', borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', borderLeft: '4px solid #3b82f6',
            minWidth: 220, pointerEvents: 'none'
          };
          const row = (label, value, color) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, background: color, borderRadius: 3 }} />
                <span style={{ color: '#666', fontSize: 13 }}>{label}:</span>
              </div>
              <span style={{ color: '#242424', fontWeight: 600, fontSize: 13 }}>{(value ?? 0).toLocaleString()}</span>
            </div>
          );
          return (
            <div style={cardStyle}>
              <div style={{ fontWeight: 700, color: '#242424', marginBottom: 12, fontSize: 16 }}>{category}</div>
              {row('Users Online (Demand)', userVal, '#3b82f6')}
              {row('Consultants Online (Supply)', consultantVal, '#10b981')}
              {row('Missed Consultations', missedVal, '#f59e0b')}
              {row('Cancelled Consultations', cancelledVal, '#ef4444')}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#666', fontSize: 13, fontWeight: 600 }}>Demand/Supply Ratio (Current):</span>
                  <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13 }}>{demandSupplyRatio > 0 ? demandSupplyRatio.toFixed(2) : '0.00'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#666', fontSize: 13, fontWeight: 600 }}>Average Demand/Supply Ratio (Overall):</span>
                  <span style={{ color: '#10b981', fontWeight: 700, fontSize: 13 }}>{overallRatio > 0 ? overallRatio.toFixed(2) : '0.00'}</span>
                </div>
              </div>
            </div>
          );
        })()}
        <ReactApexChart options={options} series={series} type="line" height={400} />
      </div>
    </div>
  );
};

export default UserConsultantOnlineGapChart;

